/**
 * Types store for managing types data
 * 
 * This store handles caching and syncing of types for games,
 * using the GenericStore pattern for persistent caching.
 */

import { storeRegistry } from "@/rerun-manager/GenericStore";
import { apiService } from "@/lib/api-functions/api-service";
import { generateUUID } from "@/lib/utils/uuid";

// Value type for type elements - determines what kind of numeric data the element holds
export type ElementValueType = 'none' | 'single' | 'range';

// Type element interface for nested elements (from API)
export interface TypeElement {
  id: string;
  name: string;
  description?: string;
  // Numeric value support for variables
  valueType?: ElementValueType;  // Default: 'none' - no numeric value
  numericValue?: number;         // For single value (when valueType is 'single')
  rangeMin?: number;             // For range minimum (when valueType is 'range')
  rangeMax?: number;             // For range maximum (when valueType is 'range')
  increment?: number;            // For range stepping/increment (when valueType is 'range')
  // Linked value support - allows linking to another variable's element
  linkedVariableId?: string;     // The ID of the variable this element is linked to
  linkedElementId?: string;      // The ID of the specific element within that variable
}

// Type element interface for standalone elements (with type_id)
export interface TypeElementWithTypeId {
  id: string;
  type_id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  // Numeric value support
  valueType?: ElementValueType;
  numericValue?: number;
  rangeMin?: number;
  rangeMax?: number;
  increment?: number;
  // Linked value support
  linkedVariableId?: string;
  linkedElementId?: string;
}

// Type interface matching the API response
export interface TypeItem {
  id: string;
  game_id: string;
  name: string;
  element_type: string; // text, boolean, number, etc.
  group_name?: string; // Optional group for organizing types
  description?: string;
  created_at: string;
  updated_at: string;
  data?: TypeElement[]; // Elements belonging to this type
  folder?: string; // Folder ID for organization in Variables page
}

/**
 * Store for types, using GenericStore
 */
export const typesStore = storeRegistry.getStore<TypeItem>({
  key: 'types',
  ttlMs: 1000 * 60 * 10, // 10 minutes cache
});

/**
 * Fetch types for a specific game from the API and sync the store
 * @param gameId - The game ID to fetch types for
 * @param toast - Optional toast function for error reporting
 */
export async function fetchTypes(
  gameId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<TypeItem[]> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Fetch data from API
    const result = await apiService.fetchTypes(gameId);
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to fetch types');
    }

    const types = result.data;

    // Sync the store with the fetched data
    await typesStore.syncFromRemote(async () => types);

    return types;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch types';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Create a new type via API and sync the store
 * @param typeData - The type data to create
 * @param toast - Optional toast function for error reporting
 */
export async function createType(
  typeData: {
    game_id: string;
    name: string;
    element_type: string;
    group?: string;
    data: TypeElement[];
    description?: string;
    folder?: string;
  },
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<TypeItem> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Create data via API - map group to group_name for API
    const apiData = {
      ...typeData,
      group: typeData.group // The API expects 'group' but stores it as 'group_name'
    };
    const result = await apiService.createType(apiData);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to create type');
    }

    const newType = result.data;

    // Add the new type to the store
    const currentData = typesStore.getData();
    const updatedData = [...currentData, newType];
    typesStore.setData(updatedData);

    return newType;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to create type';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Update an existing type via API and sync the store
 * @param typeData - The type data to update
 * @param toast - Optional toast function for error reporting
 */
export async function updateType(
  typeData: {
    id: string;
    game_id: string;
    name: string;
    element_type: string;
    group?: string;
    description?: string;
    data: TypeElement[];
    folder?: string;
  },
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<TypeItem> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Update data via API - map group to group_name for API
    const apiData = {
      ...typeData,
      group: typeData.group // The API expects 'group' but stores it as 'group_name'
    };
    const result = await apiService.updateType(apiData);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update type');
    }

    const updatedType = result.data;

    // Update the type in the store
    const currentData = typesStore.getData();
    const updatedData = currentData.map(item => 
      item.id === typeData.id ? updatedType : item
    );
    typesStore.setData(updatedData);

    return updatedType;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to update type';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Delete a type via API and sync the store
 * @param id - The type ID to delete
 * @param gameId - The game ID
 * @param toast - Optional toast function for error reporting
 */
export async function deleteType(
  id: string,
  gameId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<boolean> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Delete data via API
    const result = await apiService.deleteType(id, gameId);
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to delete type');
    }

    // Remove the type from the store
    const currentData = typesStore.getData();
    const filteredData = currentData.filter(item => item.id !== id);
    typesStore.setData(filteredData);

    return true;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to delete type';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Get cached types for a specific game
 * @param gameId - The game ID to get types for
 */
export function getCachedTypes(gameId: string): TypeItem[] {
  const allTypes = typesStore.getData();
  return allTypes.filter(item => item.game_id === gameId);
}

/**
 * Clear types cache
 */
export async function clearTypesCache() {
  try {
    await typesStore.clearCache();
    console.log('Types cache cleared successfully');
  } catch (error) {
    console.error('Failed to clear types cache:', error);
  }
}

/**
 * Create a new type element via API and sync the store
 * @param elementData - The element data to create
 * @param toast - Optional toast function for error reporting
 */
export async function createTypeElement(
  elementData: {
    type_id: string;
    name: string;
    description?: string;
    valueType?: ElementValueType;
    numericValue?: number;
    rangeMin?: number;
    rangeMax?: number;
    increment?: number;
    linkedVariableId?: string;
    linkedElementId?: string;
  },
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<TypeElement> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Generate a UUID for the new element
    const newElementId = generateUUID();
    
    // Create the new element
    const newElement: TypeElement = {
      id: newElementId,
      name: elementData.name,
      description: elementData.description,
      valueType: elementData.valueType,
      numericValue: elementData.numericValue,
      rangeMin: elementData.rangeMin,
      rangeMax: elementData.rangeMax,
      increment: elementData.increment,
      linkedVariableId: elementData.linkedVariableId,
      linkedElementId: elementData.linkedElementId,
    };

    // Get the current type and update its data
    const currentData = typesStore.getData();
    const typeToUpdate = currentData.find(type => type.id === elementData.type_id);
    
    if (!typeToUpdate) {
      throw new Error('Type not found');
    }

    // Update the type with the new element
    const updatedType = {
      ...typeToUpdate,
      data: [...(typeToUpdate.data || []), newElement]
    };

    console.log('createTypeElement - updatedType:', updatedType);

    // Update the type via API
    const result = await apiService.updateType(updatedType);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to create type element');
    }

    // Update the store
    const updatedData = currentData.map(type => 
      type.id === elementData.type_id ? result.data : type
    );
    typesStore.setData(updatedData);

    return newElement;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to create type element';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Update an existing type element via API and sync the store
 * @param elementData - The element data to update
 * @param toast - Optional toast function for error reporting
 */
export async function updateTypeElement(
  elementData: {
    id: string;
    type_id: string;
    name: string;
    description?: string;
    valueType?: ElementValueType;
    numericValue?: number;
    rangeMin?: number;
    rangeMax?: number;
    increment?: number;
    linkedVariableId?: string;
    linkedElementId?: string;
  },
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<TypeElement> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Get the current type and update the specific element
    const currentData = typesStore.getData();
    const typeToUpdate = currentData.find(type => type.id === elementData.type_id);
    
    if (!typeToUpdate) {
      throw new Error('Type not found');
    }

    // Update the specific element in the type's data
    const updatedData = (typeToUpdate.data || []).map(element => 
      element.id === elementData.id ? {
        ...element,
        name: elementData.name,
        description: elementData.description,
        valueType: elementData.valueType,
        numericValue: elementData.numericValue,
        rangeMin: elementData.rangeMin,
        rangeMax: elementData.rangeMax,
        increment: elementData.increment,
        linkedVariableId: elementData.linkedVariableId,
        linkedElementId: elementData.linkedElementId,
      } : element
    );

    // Update the type with the modified data
    const updatedType = {
      ...typeToUpdate,
      data: updatedData
    };

    // Update the type via API
    const result = await apiService.updateType(updatedType);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update type element');
    }

    // Update the store
    const newData = currentData.map(type => 
      type.id === elementData.type_id ? result.data : type
    );
    typesStore.setData(newData);

    // Return the updated element
    const updatedElement = updatedData.find(element => element.id === elementData.id);
    if (!updatedElement) {
      throw new Error('Updated element not found');
    }

    return updatedElement;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to update type element';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Delete a type element via API and sync the store
 * @param id - The element ID to delete
 * @param typeId - The type ID
 * @param toast - Optional toast function for error reporting
 */
export async function deleteTypeElement(
  id: string,
  typeId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<boolean> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Get the current type and remove the specific element
    const currentData = typesStore.getData();
    const typeToUpdate = currentData.find(type => type.id === typeId);
    
    if (!typeToUpdate) {
      throw new Error('Type not found');
    }

    // Remove the specific element from the type's data
    const updatedData = (typeToUpdate.data || []).filter(element => element.id !== id);

    // Update the type with the modified data
    const updatedType = {
      ...typeToUpdate,
      data: updatedData
    };

    // Update the type via API
    const result = await apiService.updateType(updatedType);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to delete type element');
    }

    // Update the store
    const newData = currentData.map(type => 
      type.id === typeId ? result.data : type
    );
    typesStore.setData(newData);

    return true;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to delete type element';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
} 